import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import api, { getAvatarUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useProjectRealtime } from '../lib/useRealtime';
import {
  CARD_LABEL_COLORS, PRIORITY_LABEL, PRIORITY_COLORS, BOARD_BACKGROUNDS,
  backgroundStyle, dueChipClass, fmtDate, initials, priorityKey, TASK_STATUS_LABEL, TASK_STATUS_COLORS
} from '../lib/constants';
import TaskDetail from '../components/TaskDetail';
import ChatWidget from '../components/ChatWidget';
import CalendarView from '../components/CalendarView';
import TimelineView from '../components/TimelineView';
import FilterPanel, { EMPTY_FILTERS, filterMatches, activeFilterCount } from '../components/FilterPanel';
import Portal from '../components/Portal';
import {
  Plus, X, Play, Square, Check, Trash2, ChevronLeft, ChevronDown, MoreHorizontal, Calendar,
  MessageSquare, Paperclip, Tag, User as UserIcon, Image as ImageIcon, Star, AlertCircle, CalendarDays, GanttChart, Filter as FilterIcon, Pencil, Link2, Users, Search,
} from 'lucide-react';

function Avatar({ user, size = 28, title }) {
  return (
    <div className="tm-avatar" title={title || user?.full_name || user?.email} style={{
      width: size, height: size, fontSize: size / 2.5,
      background: user?.avatar_url ? `url(${getAvatarUrl(user.avatar_url)}) center/cover` : (user?.avatar_color || 'var(--trello-text-muted)'),
      border: '2px solid #fff',
    }}>{!user?.avatar_url && initials(user)}</div>
  );
}

function fmtElapsed(s) {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export default function ProjectBoard() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);
  const [lists, setLists] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [addingIn, setAddingIn] = useState(null);
  const [addingList, setAddingList] = useState(false);
  const [showBg, setShowBg] = useState(false);
  const [showSwitch, setShowSwitch] = useState(false);
  const [otherBoards, setOtherBoards] = useState([]);
  const [dragOver, setDragOver] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [listDragging, setListDragging] = useState(null);
  const [listDragOver, setListDragOver] = useState(null);
  const [tick, setTick] = useState(0);
  const [view, setView] = useState('board'); // board | calendar | timeline
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [renamingProject, setRenamingProject] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [projectChannel, setProjectChannel] = useState(null);
  const [dateMarkings, setDateMarkings] = useState([]);
  const [marketingTags, setMarketingTags] = useState([]);
  const [addingTask, setAddingTask] = useState(false);

  const [showProjectMembersModal, setShowProjectMembersModal] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [allCompanyUsers, setAllCompanyUsers] = useState([]);
  const [projectMemberSearchQuery, setProjectMemberSearchQuery] = useState('');

  const loadAllUsers = async () => {
    try {
      const { data } = await api.get('/team');
      setAllCompanyUsers(data.members || []);
    } catch (e) {
      console.error('Error loading company users', e);
    }
  };

  useEffect(() => {
    if (showProjectMembersModal || showEditProject) {
      loadAllUsers();
    }
  }, [showProjectMembersModal, showEditProject]);

  const handleAddProjectMember = async (userId) => {
    try {
      await api.post(`/projects/${id}/members`, { user_id: userId });
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to add project member');
    }
  };

  const handleRemoveProjectMember = async (userId) => {
    try {
      await api.delete(`/projects/${id}/members/${userId}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to remove project member');
    }
  };

  const toggleStar = async () => {
    if (!project) return;
    try {
      const newStarred = project.starred ? 0 : 1;
      const { data } = await api.patch(`/projects/${id}`, { starred: newStarred });
      setProject(data.project);
    } catch (e) {
      console.error('Failed to toggle star:', e);
    }
  };

  const load = async () => {
    try {
      setError(null);
      const [p, ls, t, a, dm, mt] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/lists/project/${id}`),
        api.get(`/tasks?project_id=${id}`),
        api.get('/timer/active'),
        api.get(`/projects/${id}/date-markings`),
        api.get(`/projects/${id}/marketing-tags`),
      ]);
      setProject(p.data.project); setMembers(p.data.members);
      setLists(ls.data.lists); setTasks(t.data.tasks); setActive(a.data.active);
      setDateMarkings(dm.data.markings || []);
      setMarketingTags(mt.data.tags || []);
      setProjectName(p.data.project.name);

      try {
        const { data } = await api.get('/chat/channels');
        const ch = data.channels.find(c => c.project_id === id);
        if (ch) setProjectChannel(ch);
      } catch (e) {
        console.error('Error fetching project chat channel', e);
      }
    } catch (err) {
      console.error('Error loading project details', err);
      if (err.response?.status === 403) {
        setError("you don't have permition to see the project");
      } else {
        setError(err.response?.data?.error || "Failed to load project");
      }
    }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    const taskParam = searchParams.get('task');
    if (taskParam && (!detail || detail.id !== taskParam)) {
      setDetail({ id: taskParam });
    }
  }, [searchParams]);

  // When user closes the modal, strip ?task= from URL so refresh doesn't re-open.
  const closeDetail = () => {
    setDetail(null);
    if (searchParams.get('task')) {
      const next = new URLSearchParams(searchParams);
      next.delete('task');
      setSearchParams(next, { replace: true });
    }
  };
  useEffect(() => { const i = setInterval(() => setTick(x => x + 1), 1000); return () => clearInterval(i); }, []);

  // Realtime updates from WebSocket
  useProjectRealtime(id, (msg) => {
    if (msg.event === 'task_created') {
      setTasks((prev) => prev.some((t) => t.id === msg.task.id) ? prev : [msg.task, ...prev]);
    } else if (msg.event === 'task_updated') {
      setTasks((prev) => prev.map((t) => t.id === msg.task.id ? { ...t, ...msg.task } : t));
    } else if (msg.event === 'task_deleted') {
      setTasks((prev) => prev.filter((t) => t.id !== msg.task_id));
    } else if (msg.event === 'list_created') {
      setLists((prev) => prev.some((l) => l.id === msg.list.id) ? prev : [...prev, msg.list].sort((a, b) => (a.position || 0) - (b.position || 0)));
    } else if (msg.event === 'list_updated') {
      setLists((prev) => prev.map((l) => l.id === msg.list.id ? { ...l, ...msg.list } : l).sort((a, b) => (a.position || 0) - (b.position || 0)));
    } else if (msg.event === 'list_deleted') {
      setLists((prev) => prev.filter((l) => l.id !== msg.list_id));
    } else if (msg.event === 'lists_reordered') {
      const posMap = Object.fromEntries(msg.order.map((o) => [o.id, o.position]));
      setLists((prev) => prev.map((l) => posMap[l.id] !== undefined ? { ...l, position: posMap[l.id] } : l)
        .sort((a, b) => (a.position || 0) - (b.position || 0)));
    } else if (msg.event === 'comment_created' || msg.event === 'comment_deleted') {
      load();
    }
  });


  const isLeader = user.role === 'leader';
  const isManager = project && project.delegator_id === user.id;
  const canManageBoard = isLeader || isManager;

  const filteredTasks = React.useMemo(
    () => tasks.filter((t) => filterMatches(t, filters, user.id)),
    [tasks, filters, user.id]);
  const filterCount = activeFilterCount(filters);

  const saveProjectName = async () => {
    const trimmed = projectName.trim();
    if (!trimmed || trimmed === project.name) { setRenamingProject(false); setProjectName(project.name); return; }
    try { await api.patch(`/projects/${id}`, { name: trimmed }); setRenamingProject(false); load(); }
    catch (e) { alert(e.response?.data?.error || 'Rename failed'); setRenamingProject(false); }
  };

  const addTask = async (list, title) => {
    if (!title.trim() || addingTask) return;
    setAddingTask(true);
    try {
      await api.post('/tasks', { project_id: id, list_id: list.id, title: title.trim() });
      setAddingIn(null);
      await load();
    } catch (err) {
      console.error('Error adding task:', err);
      alert(err.response?.data?.error || 'Failed to add card');
    } finally {
      setAddingTask(false);
    }
  };

  const handleCalendarAddTask = async (date, title, listId) => {
    if (!title.trim() || !listId || addingTask) return;
    setAddingTask(true);
    try {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      await api.post('/tasks', {
        project_id: id,
        list_id: listId,
        title: title.trim(),
        due_date: dateString
      });
      await load();
    } catch (err) {
      console.error('Error adding calendar task:', err);
      alert(err.response?.data?.error || 'Failed to add task to calendar');
    } finally {
      setAddingTask(false);
    }
  };

  const handleUpdateDateMarking = async (date, tagId, action) => {
    try {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      await api.post(`/projects/${id}/date-markings`, {
        date: dateString,
        tag_id: tagId,
        action
      });

      const dm = await api.get(`/projects/${id}/date-markings`);
      setDateMarkings(dm.data.markings || []);
    } catch (err) {
      console.error('Error updating date marking:', err);
    }
  };

  const handleCreateMarketingTag = async (name, color) => {
    try {
      await api.post(`/projects/${id}/marketing-tags`, { name, color });
      const mt = await api.get(`/projects/${id}/marketing-tags`);
      setMarketingTags(mt.data.tags || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create marketing tag');
    }
  };

  const handleEditMarketingTag = async (tagId, name, color) => {
    try {
      await api.put(`/projects/${id}/marketing-tags/${tagId}`, { name, color });

      const [mt, dm] = await Promise.all([
        api.get(`/projects/${id}/marketing-tags`),
        api.get(`/projects/${id}/date-markings`)
      ]);
      setMarketingTags(mt.data.tags || []);
      setDateMarkings(dm.data.markings || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update marketing tag');
    }
  };

  const handleDeleteMarketingTag = async (tagId) => {
    try {
      await api.delete(`/projects/${id}/marketing-tags/${tagId}`);

      const [mt, dm] = await Promise.all([
        api.get(`/projects/${id}/marketing-tags`),
        api.get(`/projects/${id}/date-markings`)
      ]);
      setMarketingTags(mt.data.tags || []);
      setDateMarkings(dm.data.markings || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete marketing tag');
    }
  };

  const moveTask = async (taskId, newListId) => {
    const t = tasks.find((x) => x.id === taskId);
    if (!t || t.list_id === newListId) return;
    setTasks((prev) => prev.map((x) => x.id === taskId ? { ...x, list_id: newListId } : x));
    try { await api.patch(`/tasks/${taskId}`, { list_id: newListId }); }
    catch (err) {
      setTasks((prev) => prev.map((x) => x.id === taskId ? { ...x, list_id: t.list_id } : x));
      alert(err.response?.data?.error || 'Move failed');
    }
  };

  const toggleTimer = async (taskId) => {
    if (active) await api.post('/timer/stop');
    if (!active || active.task_id !== taskId) await api.post('/timer/start', { task_id: taskId });
    load();
  };

  const openSwitch = async () => {
    if (!otherBoards.length) {
      const { data } = await api.get('/projects');
      setOtherBoards(data.projects.filter((p) => p.id !== id));
    }
    setShowSwitch(true);
  };

  const setBackground = async (bg) => {
    await api.patch(`/projects/${id}`, { background_type: bg.type, background_url: bg.type === 'gradient' ? bg.name : bg.url });
    setShowBg(false); load();
  };

  const reorderLists = async (draggedId, targetId) => {
    if (draggedId === targetId) return;
    const sorted = [...lists].sort((a, b) => (a.position || 0) - (b.position || 0));
    const dragIdx = sorted.findIndex((l) => l.id === draggedId);
    const tgtIdx = sorted.findIndex((l) => l.id === targetId);
    if (dragIdx < 0 || tgtIdx < 0) return;
    const [moved] = sorted.splice(dragIdx, 1);
    sorted.splice(tgtIdx, 0, moved);
    const order = sorted.map((l, i) => ({ id: l.id, position: i }));
    setLists(sorted.map((l, i) => ({ ...l, position: i })));
    try { await api.post('/lists/reorder', { project_id: id, order }); }
    catch (e) { load(); }
  };

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#F4F5F7', color: '#172B4D', fontFamily: 'sans-serif', padding: 20 }}>
        <div style={{ background: '#fff', borderRadius: 8, padding: '32px 40px', boxShadow: '0 4px 12px rgba(9, 30, 66, 0.15)', maxWidth: 440, width: '100%', textAlign: 'center' }} data-testid="error-message">
          <h2 style={{ fontSize: 20, fontWeight: 600, color: '#DE350B', margin: '0 0 12px' }}>Access Denied</h2>
          <p style={{ fontSize: 14, color: '#5E6C84', margin: '0 0 24px', lineHeight: 1.5 }}>{error}</p>
          <Link to="/projects" className="tm-btn" style={{ textDecoration: 'none', background: 'var(--trello-blue)', color: '#fff', padding: '10px 20px', borderRadius: 4, fontWeight: 600 }}>Back to Projects</Link>
        </div>
      </div>
    );
  }

  if (!project) return <div className="p-8 text-center text-white"><div className="tm-loader" style={{ margin: '0 auto' }} /></div>;

  return (
    <div className="tm-project-bg" style={backgroundStyle(project)}>
      {/* Board header */}
      <div style={{ padding: '12px 20px', color: '#fff', display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(8px)' }}>
        <Link to="/projects" className="tm-btn-ghost-light" data-testid="back-to-projects" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ChevronLeft size={16} />Boards
        </Link>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <h1
            style={{ fontSize: 20, fontWeight: 700, margin: 0, cursor: canManageBoard ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 6px', borderRadius: 4 }}
            onClick={() => canManageBoard && setShowEditProject(true)}
            title={canManageBoard ? 'Click to edit board' : undefined}
            data-testid="project-name"
          >
            {project.name}
            {canManageBoard && <Pencil size={12} style={{ opacity: 0.7 }} />}
          </h1>
          <button
            // aakash

            onClick={toggleStar}
            className="tm-btn-ghost-light"
            style={{ display: 'none', padding: '6px', minWidth: 0, alignItems: 'center', justifyContent: 'center', background: 'transparent' }}
            // style={{ display: 'none', padding: '6px', minWidth: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent' }}
            title={project.starred ? 'Unstar board' : 'Star board'}
            data-testid="project-star-btn"
          >
            <Star size={16} fill={project.starred ? '#F2D600' : 'none'} stroke={project.starred ? '#F2D600' : '#fff'} style={{ transition: 'all 0.2s ease' }} />
          </button>
        </div>
        <div style={{ display: 'flex', gap: 4, marginLeft: 8, background: 'rgba(255,255,255,0.12)', borderRadius: 6, padding: 2 }} data-testid="view-switcher">
          {[
            { k: 'board', l: 'Board', Icon: Star },
            { k: 'list', l: 'List', Icon: MessageSquare },
            { k: 'calendar', l: 'Calendar', Icon: CalendarDays },
            { k: 'timeline', l: 'Timeline', Icon: GanttChart },
          ].map(({ k, l, Icon }) => (
            <button key={k} onClick={() => setView(k)} data-testid={`view-${k}`}
              style={{ background: view === k ? 'rgba(255,255,255,0.28)' : 'transparent', border: 'none', color: '#fff', padding: '4px 10px', borderRadius: 4, fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Icon size={13} /> {l}
            </button>
          ))}
        </div>
        <button className="tm-btn-ghost-light" onClick={() => setShowFilter((v) => !v)} data-testid="filter-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: filterCount > 0 ? 'rgba(255,255,255,0.28)' : undefined }}>
          <FilterIcon size={14} />
          Filter{filterCount > 0 && <span style={{ marginLeft: 4, background: '#F2D600', color: 'var(--trello-text)', fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 8 }} data-testid="filter-btn-count">{filterCount}</span>}
        </button>
        <div style={{ flex: 1 }} />
        <div className="flex -space-x-2 items-center cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setShowProjectMembersModal(true)} title="Manage Project Members">
          {members.slice(0, 5).map(m => <Avatar key={m.id} user={m} size={30} />)}
          {members.length > 5 && <div className="tm-avatar" style={{ width: 30, height: 30, fontSize: 11, background: 'var(--trello-text-muted)', border: '2px solid #fff' }}>+{members.length - 5}</div>}
        </div>
        {canManageBoard && (
          <button className="tm-btn-ghost-light" onClick={() => setShowBg((v) => !v)} data-testid="board-bg-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <ImageIcon size={14} />Background
          </button>
        )}
        {/* {projectChannel && (
          <button className="tm-btn-ghost-light" onClick={() => setShowChat((v) => !v)} data-testid="board-chat-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: showChat ? 'rgba(255,255,255,0.28)' : undefined }}>
            <MessageSquare size={14} />Chat
          </button>
        )} */}
      </div>


      {/* Background picker */}
      {showBg && (
        <Portal>
          <div className="tm-modal-overlay" onClick={() => setShowBg(false)}>
            <div className="tm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }} data-testid="bg-picker">
              <div className="flex justify-between items-center mb-4"><h3 style={{ fontWeight: 700, margin: 0 }}>Choose a background</h3>
                <button onClick={() => setShowBg(false)} className="tm-btn-ghost"><X size={18} /></button></div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {BOARD_BACKGROUNDS.map((bg) => (
                  <button key={bg.name} onClick={() => setBackground(bg)} data-testid={`bg-${bg.name}`}
                    style={{ background: bg.preview, aspectRatio: '4/3', borderRadius: 6, cursor: 'pointer', border: '2px solid transparent', position: 'relative', color: '#fff', fontSize: 11, fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', padding: 6 }}>{bg.name}</button>
                ))}
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Project Members Modal */}
      {showProjectMembersModal && (
        <Portal>
          <div className="tm-modal-overlay" onClick={() => setShowProjectMembersModal(false)}>
            <div className="tm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440, padding: 0 }} data-testid="project-members-modal">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--trello-border)' }}>
                <h3 style={{ fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Users size={18} style={{ color: 'var(--trello-blue)' }} /> Manage Board Members
                </h3>
                <button onClick={() => setShowProjectMembersModal(false)} className="tm-btn-ghost" style={{ padding: 4 }}><X size={18} /></button>
              </div>

              <div style={{ padding: 20 }}>
                <div style={{ position: 'relative', marginBottom: 16 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#A0AEC0' }} />
                  <input
                    type="text"
                    placeholder="Search company members..."
                    value={projectMemberSearchQuery}
                    onChange={e => setProjectMemberSearchQuery(e.target.value)}
                    className="tm-input"
                    style={{ paddingLeft: 32 }}
                  />
                </div>

                <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--trello-border)', borderRadius: 4 }}>
                  {allCompanyUsers
                    .filter(u => u.is_active && u.status !== 'deleted' && u.status !== 'pending')
                    .filter(u => u.full_name?.toLowerCase().includes(projectMemberSearchQuery.toLowerCase()) || u.email?.toLowerCase().includes(projectMemberSearchQuery.toLowerCase()))
                    .map(u => {
                      const isMember = members.some(m => m.id === u.id);
                      const canEdit = user.role === 'leader' || (project && project.delegator_id === user.id);
                      return (
                        <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid #EDF2F7' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div className="tm-avatar" style={{ width: 28, height: 28, fontSize: 11, background: u.avatar_color || 'var(--trello-text-muted)' }}>
                              {u.full_name?.slice(0, 2).toUpperCase() || 'U'}
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--trello-text)' }}>{u.full_name}</div>
                              <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', textTransform: 'capitalize' }}>{u.role}</div>
                            </div>
                          </div>
                          {canEdit ? (
                            isMember ? (
                              u.role === 'leader' ? (
                                <span style={{ fontSize: 11, color: 'var(--trello-text-muted)', fontWeight: 600 }}>Leader</span>
                              ) : (
                                <button
                                  onClick={() => handleRemoveProjectMember(u.id)}
                                  className="tm-btn"
                                  style={{ background: 'var(--trello-list-bg)', color: '#DE350B', padding: '4px 8px', fontSize: 12 }}
                                >
                                  Remove
                                </button>
                              )
                            ) : (
                              <button
                                onClick={() => handleAddProjectMember(u.id)}
                                className="tm-btn"
                                style={{ background: 'var(--trello-list-bg)', color: '#00B8D9', padding: '4px 8px', fontSize: 12 }}
                              >
                                Add
                              </button>
                            )
                          ) : (
                            isMember && (
                              <span style={{ fontSize: 11, color: 'var(--trello-text-muted)', fontWeight: 600 }}>Member</span>
                            )
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>

              <div style={{ padding: '16px 20px', borderTop: '1px solid var(--trello-border)', display: 'flex', justifyContent: 'end', background: 'var(--trello-list-bg)' }}>
                <button onClick={() => setShowProjectMembersModal(false)} className="tm-btn">
                  Close
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {showEditProject && (
        <EditProjectModal
          project={project}
          members={members}
          allUsers={allCompanyUsers}
          onClose={() => setShowEditProject(false)}
          onSaved={() => {
            setShowEditProject(false);
            load();
          }}
        />
      )}

      {/* Lists (board view) or Calendar / Timeline / List */}
      <div className="flex-1 flex overflow-hidden" style={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}>
        <div className="flex-1 overflow-auto h-full" style={{ flex: 1, overflow: 'auto' }}>
          {view === 'calendar' && (
            <CalendarView
              tasks={filteredTasks}
              onOpenTask={(id) => setDetail({ id })}
              onAddTask={handleCalendarAddTask}
              lists={lists}
              dateMarkings={dateMarkings}
              onUpdateMarking={handleUpdateDateMarking}
              marketingTags={marketingTags}
              onCreateTag={handleCreateMarketingTag}
              onEditTag={handleEditMarketingTag}
              onDeleteTag={handleDeleteMarketingTag}
            />
          )}
          {view === 'timeline' && <TimelineView tasks={filteredTasks} onOpenTask={(id) => setDetail({ id })} onUpdate={load} />}
          {view === 'list' && (
            <div style={{ padding: '20px 24px 80px' }}>
              <div className="tm-stat-card" style={{ borderLeft: 'none' }} data-testid="list-view">
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--trello-text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      <th style={{ padding: '8px 4px', width: 24 }}></th>
                      <th style={{ padding: '8px 4px' }}>Task</th>
                      <th>List</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Assignees</th>
                      <th>Start</th>
                      <th>Due</th>
                      <th>Est. hrs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTasks.map((t) => {
                      const listName = (lists.find((l) => l.id === t.list_id) || {}).name || '—';
                      return (
                        <tr key={t.id} data-testid={`list-row-${t.id}`}
                          onClick={() => setDetail({ id: t.id })}
                          style={{ borderTop: '1px solid var(--trello-border)', cursor: 'pointer' }}
                          className="hover:bg-slate-50">
                          <td style={{ padding: '10px 4px' }}>
                            {t.label_color && <span style={{ display: 'inline-block', width: 6, height: 20, borderRadius: 2, background: t.label_color }} />}
                          </td>
                          <td style={{ padding: '10px 4px', fontWeight: 500, maxWidth: 320 }}>{t.title}</td>
                          <td style={{ fontSize: 12 }}>{listName}</td>
                          <td><span className="tm-pill" style={{ background: 'var(--trello-list-bg)', color: 'var(--trello-text)' }}>{t.task_status || 'new'}</span></td>
                          <td><span className={`tm-pill tm-pill-${priorityKey(t)}`}>{PRIORITY_LABEL[priorityKey(t)]}</span></td>
                          <td>
                            <div className="flex -space-x-1">
                              {(t.assignees || []).slice(0, 3).map((a) => (
                                <div key={a.id} className="tm-avatar" style={{ width: 22, height: 22, fontSize: 10, background: a.avatar_color || 'var(--trello-text-muted)', border: '2px solid #fff' }}>
                                  {initials(a)}
                                </div>
                              ))}
                              {(t.assignees || []).length === 0 && <span style={{ color: 'var(--trello-text-muted)' }}>—</span>}
                            </div>
                          </td>
                          <td style={{ fontSize: 12 }}>{t.start_date ? fmtDate(t.start_date) : '—'}</td>
                          <td style={{ fontSize: 12 }}>{t.due_date ? fmtDate(t.due_date) : '—'}</td>
                          <td style={{ fontSize: 12 }}>{t.estimated_hours ?? '—'}</td>
                        </tr>
                      );
                    })}
                    {filteredTasks.length === 0 && (
                      <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: 'var(--trello-text-muted)' }}>No tasks match your filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {view === 'board' && (
            <div className="tm-board-scroll flex gap-4" style={{ paddingBottom: 80 }}>
              {lists.map((list) => {
                const list_tasks = filteredTasks.filter((t) => t.list_id === list.id).sort((a, b) => (a.position || 0) - (b.position || 0));
                const isOver = dragOver === list.id;
                const isListOver = listDragOver === list.id && listDragging && listDragging !== list.id;
                return (
                  <div className="tm-list" key={list.id} data-testid={`list-${list.id}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (listDragging) {
                        if (listDragOver !== list.id) setListDragOver(list.id);
                      } else if (dragOver !== list.id) setDragOver(list.id);
                    }}
                    onDragLeave={(e) => { if (e.currentTarget === e.target) { setDragOver(null); setListDragOver(null); } }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const tid = e.dataTransfer.getData('text/task-id');
                      const lid = e.dataTransfer.getData('text/list-id');
                      setDragOver(null); setDragging(null); setListDragOver(null);
                      if (lid && lid !== list.id) { reorderLists(lid, list.id); setListDragging(null); return; }
                      if (tid) moveTask(tid, list.id);
                    }}
                    style={{
                      outline: isOver ? '2px dashed #026AA7' : (isListOver ? '2px dashed #F2D600' : 'none'),
                      opacity: listDragging === list.id ? 0.5 : 1,
                      transform: isListOver ? 'translateX(6px)' : 'none',
                      transition: 'transform 0.15s',
                    }}>
                    <ListHeader list={list} count={list_tasks.length} canManage={canManageBoard} onChange={load} isLeader={isLeader}
                      onDragStart={(e) => { e.dataTransfer.setData('text/list-id', list.id); e.dataTransfer.effectAllowed = 'move'; setListDragging(list.id); }}
                      onDragEnd={() => { setListDragging(null); setListDragOver(null); }} />
                    <div className="tm-list-scroll">
                      {list_tasks.map((t) => (
                        <TaskCard key={t.id} task={t} tasks={tasks} active={active} onOpen={() => setDetail(t)} onTimer={() => toggleTimer(t.id)}
                          dragging={dragging === t.id}
                          onDragStart={(e) => { e.dataTransfer.setData('text/task-id', t.id); e.dataTransfer.effectAllowed = 'move'; setDragging(t.id); }}
                          onDragEnd={() => { setDragging(null); setDragOver(null); }} />
                      ))}
                    </div>
                    <div style={{ marginTop: 12, padding: '0 4px', flexShrink: 0 }}>
                      {addingIn === list.id ? (
                        <QuickAdd onSave={(title) => addTask(list, title)} onCancel={() => setAddingIn(null)} />
                      ) : (
                        <button className="tm-btn-ghost" style={{ width: '100%', textAlign: 'left', color: 'var(--trello-text-muted)', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                          onClick={() => setAddingIn(list.id)} data-testid={`add-card-${list.id}`}>
                          <Plus size={14} />Add a card
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Add list */}
              {canManageBoard && (
                addingList ? (
                  <AddListForm projectId={id} onDone={() => { setAddingList(false); load(); }} onCancel={() => setAddingList(false)} />
                ) : (
                  <button className="tm-add-list" onClick={() => setAddingList(true)} data-testid="add-list-btn">
                    <Plus size={16} style={{ display: 'inline', verticalAlign: -3, marginRight: 4 }} />
                    {lists.length === 0 ? 'Add first list' : 'Add another list'}
                  </button>
                )
              )}
            </div>
          )}
        </div>
        {showChat && projectChannel && (
          <div className="h-full flex-shrink-0" style={{ height: '100%' }}>
            <ChatWidget channelId={projectChannel.id} title={`${project.name} Chat`} />
          </div>
        )}
      </div>


      {/* Bottom nav */}
      <div className="tm-bottom-nav">
        <button className="active" onClick={toggleStar} data-testid="btm-board">
          <Star size={14} fill={project?.starred ? '#F2D600' : 'none'} stroke={project?.starred ? '#F2D600' : 'currentColor'} />
          Board
        </button>
        <button onClick={() => nav('/google-calendar')} data-testid="btm-calendar">📅 Calendar</button>
        {isLeader && <button onClick={() => nav('/command-center')} data-testid="btm-command">🎯 Command Center</button>}
        <button onClick={openSwitch} data-testid="btm-switch">📋 Switch boards</button>
      </div>

      {showSwitch && (
        <Portal>
          <div className="tm-switch-panel" data-testid="switch-panel">
            <div className="flex justify-between items-center mb-3">
              <h4 style={{ fontWeight: 700, margin: 0 }}>Your boards</h4>
              <button onClick={() => setShowSwitch(false)} className="tm-btn-ghost"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {otherBoards.map((p) => (
                <Link key={p.id} to={`/projects/${p.id}`} data-testid={`switch-${p.id}`}
                  onClick={() => setShowSwitch(false)}
                  style={{ background: p.color || '#0079BF', color: '#fff', borderRadius: 6, padding: 12, textDecoration: 'none', fontWeight: 600, fontSize: 14, display: 'block' }}>
                  {p.name}
                </Link>
              ))}
              {otherBoards.length === 0 && <div style={{ color: 'var(--trello-text-muted)', fontSize: 13, gridColumn: '1/-1' }}>No other boards yet.</div>}
            </div>
          </div>
        </Portal>
      )}

      {detail && <TaskDetail taskId={detail.id} members={members} project={project} currentUser={user} lists={lists}
        onClose={closeDetail} onChange={load} />}

      <FilterPanel open={showFilter} onClose={() => setShowFilter(false)}
        filters={filters} setFilters={setFilters} members={members} />
    </div>
  );
}

// -------------------------- Task card --------------------------
function TaskCard({ task, tasks, active, onOpen, onTimer, dragging, onDragStart, onDragEnd }) {
  const dueClass = dueChipClass(task);
  const cs = task.checklist_summary;

  const depIds = (() => {
    try {
      return task.dependencies_json ? JSON.parse(task.dependencies_json) : [];
    } catch {
      return [];
    }
  })();
  const blockingTasks = (tasks || []).filter(
    (t) => depIds.includes(t.id) && !t.completed && !t.deleted_at
  );
  const isBlocked = blockingTasks.length > 0;

  const blocksOthers = !task.completed && (tasks || []).some((t) => {
    if (t.completed || t.deleted_at) return false;
    try {
      const tDepIds = t.dependencies_json ? JSON.parse(t.dependencies_json) : [];
      return tDepIds.includes(task.id);
    } catch {
      return false;
    }
  });

  return (
    <div className="tm-card" data-testid={`task-${task.id}`} draggable
      onClick={onOpen} onDragStart={onDragStart} onDragEnd={onDragEnd}
      style={{
        opacity: dragging ? 0.4 : (task.completed ? 0.75 : 1),
        cursor: dragging ? 'grabbing' : 'pointer',
        paddingTop: task.label_color ? 6 : 12,
        borderLeft: task.completed ? '4px solid #61BD4F' : 'none'
      }}>
      {task.label_color && <div className="tm-card-label" style={{ background: task.label_color }} />}
      {!!task.pending_delete && (
        <div style={{ background: 'var(--trello-list-bg)', color: '#DE350B', fontSize: 11, fontWeight: 600, padding: '3px 6px', borderRadius: 3, marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <AlertCircle size={11} /> Delete pending approval
        </div>
      )}
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--trello-text)', textDecoration: task.completed ? 'line-through' : 'none' }}>
        {!!task.completed && <Check size={14} style={{ display: 'inline', color: '#61BD4F', marginRight: 4, verticalAlign: -2 }} />}
        {task.title}
      </div>

      {/* Icons row */}
      <div className="flex items-center gap-2 mt-2 flex-wrap" style={{ fontSize: 12, color: 'var(--trello-text-muted)' }}>
        <span className={`tm-pill tm-pill-${priorityKey(task)}`}>{PRIORITY_LABEL[priorityKey(task)] || priorityKey(task)}</span>
        {task.task_status && (
          <span className="tm-pill font-semibold" style={{
            background: `${TASK_STATUS_COLORS[task.task_status] || 'var(--trello-text-muted)'}15`,
            color: TASK_STATUS_COLORS[task.task_status] || 'var(--trello-text-muted)',
            border: `1px solid ${TASK_STATUS_COLORS[task.task_status] || 'var(--trello-text-muted)'}30`
          }}>
            {TASK_STATUS_LABEL[task.task_status] || task.task_status}
          </span>
        )}
        {task.completed && (
          <span className="tm-pill font-semibold bg-[var(--trello-list-bg)] text-[#137333] border-[#CEEAD6] border dark:text-emerald-400 dark:border-emerald-900/50">
            Completed
          </span>
        )}
        {dueClass && (
          <span className={dueClass}><Calendar size={11} />{fmtDate(task.due_date)}</span>
        )}
        {cs && cs.total > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }} data-testid={`card-checklist-${task.id}`}>
            <Check size={12} />{cs.done}/{cs.total}
          </span>
        )}
        {task.attachments_count > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }} data-testid={`card-att-${task.id}`}>
            <Paperclip size={11} />{task.attachments_count}
          </span>
        )}
        {isBlocked && (
          <span className="tm-pill font-semibold bg-[var(--trello-list-bg)] text-[#DE350B] border-[#FFBDAD] border inline-flex items-center gap-1 dark:text-rose-400 dark:border-rose-900/50">
            <Link2 size={11} /> Blocked
          </span>
        )}
        {blocksOthers && (
          <span className="tm-pill font-semibold bg-[var(--trello-list-bg)] text-[#00B8D9] border-[#B3F5FF] border inline-flex items-center gap-1 dark:text-cyan-400 dark:border-cyan-900/50">
            <Link2 size={11} /> Blocks
          </span>
        )}
      </div>

      {cs && cs.total > 0 && (
        <div className="tm-mini-progress mt-2"><div style={{ width: `${(cs.done / cs.total) * 100}%` }} /></div>
      )}

      {/* Bottom row: timer + assignees */}
      <div className="flex items-center justify-between mt-2" onClick={(e) => e.stopPropagation()}>
        <button className="tm-btn-ghost" style={{ padding: 4 }} onClick={onTimer} title="Timer" data-testid={`timer-${task.id}`}>
          {active?.task_id === task.id ? <Square size={14} color="#DE350B" /> : <Play size={14} />}
        </button>
        <div className="flex -space-x-2">
          {(task.assignees || []).slice(0, 3).map((a) => <Avatar key={a.id} user={a} size={24} />)}
        </div>
      </div>

      {active?.task_id === task.id && (
        <div style={{ fontSize: 12, marginTop: 6, color: '#DE350B', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          ⏱ {fmtElapsed(Math.floor((Date.now() - new Date(active.started_at).getTime()) / 1000))}
        </div>
      )}
    </div>
  );
}

// -------------------------- List header --------------------------
function ListHeader({ list, count, canManage, onChange, isLeader, onDragStart, onDragEnd }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(list.name);
  const save = async () => {
    if (name.trim() && name !== list.name) await api.patch(`/lists/${list.id}`, { name: name.trim() });
    setEditing(false); onChange();
  };
  const del = async () => {
    if (!confirm('Delete this list? Cards will be unlinked.')) return;
    await api.delete(`/lists/${list.id}`); onChange();
  };
  return (
    <div className="tm-list-header" draggable={canManage && !editing}
      onDragStart={onDragStart} onDragEnd={onDragEnd}
      style={{ cursor: canManage && !editing ? 'grab' : 'default' }}>
      {editing ? (
        <input autoFocus className="tm-input" value={name} onChange={(e) => setName(e.target.value)}
          onBlur={save} onKeyDown={(e) => e.key === 'Enter' && save()} style={{ padding: '4px 6px' }} data-testid={`list-rename-${list.id}`} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: canManage ? 'pointer' : 'default', flex: 1 }}
          onClick={() => canManage && setEditing(true)} data-testid={`list-name-${list.id}`}>
          {list.name}
          <span style={{ background: 'var(--trello-list-bg)', fontSize: 11, padding: '2px 6px', borderRadius: 10 }}>{count}</span>
        </div>
      )}
      {isLeader && !editing && list.name?.trim().toLowerCase() !== 'done' && count === 0 && (
        <button className="tm-btn-ghost" style={{ padding: 2 }} onClick={del} title="Delete list" data-testid={`list-delete-${list.id}`}><Trash2 size={12} /></button>
      )}
    </div>
  );
}

// -------------------------- Quick add --------------------------
function QuickAdd({ onSave, onCancel }) {
  const [v, setV] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSave = async () => {
    if (!v.trim() || busy) return;
    setBusy(true);
    try {
      await onSave(v.trim());
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div style={{ background: 'var(--trello-card-bg)', borderRadius: 6, padding: 8, flexShrink: 0 }}>
      <textarea autoFocus data-testid="quickadd-input" value={v} onChange={(e) => setV(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Enter a title for this card…"
        style={{ width: '100%', border: 'none', outline: 'none', resize: 'none', fontSize: 14, fontFamily: 'inherit', minHeight: 60 }} />
      <div className="flex gap-2 mt-2">
        <button className="tm-btn" onClick={handleSave} disabled={busy} data-testid="quickadd-save">
          {busy ? 'Adding…' : 'Add card'}
        </button>
        <button className="tm-btn-ghost" onClick={onCancel} disabled={busy}><X size={16} /></button>
      </div>
    </div>
  );
}

// -------------------------- Add list form --------------------------
function AddListForm({ projectId, onDone, onCancel }) {
  const [name, setName] = useState('');
  const submit = async () => {
    if (!name.trim()) return;
    try {
      await api.post('/lists', { project_id: projectId, name: name.trim() });
      onDone();
    } catch (err) {
      console.error('Error adding list:', err);
      alert(err.response?.data?.error || 'Failed to add list');
    }
  };
  return (
    <div className="tm-add-list-form">
      <input autoFocus className="tm-input" placeholder="Enter list name…" value={name}
        onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} data-testid="new-list-input" />
      <div className="flex gap-2 mt-2">
        <button className="tm-btn" onClick={submit} data-testid="new-list-save">Add list</button>
        <button className="tm-btn-ghost" onClick={onCancel}><X size={16} /></button>
      </div>
    </div>
  );
}

// -------------------------- Edit project modal --------------------------
function EditProjectModal({ project, members, allUsers, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: project.name || '',
    description: project.description || '',
    color: project.color || '#0079BF',
    background_type: project.background_type || 'color',
    background_url: project.background_url || '',
    delegator_id: project.delegator_id || '',
    member_ids: members.map(m => m.id),
  });
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [showOnlySelectedMembers, setShowOnlySelectedMembers] = useState(false);

  const toggleMember = (id) => {
    setForm(prev => ({
      ...prev,
      member_ids: prev.member_ids.includes(id)
        ? prev.member_ids.filter(x => x !== id)
        : [...prev.member_ids, id]
    }));
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBanner(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({
        ...prev,
        color: '',
        background_type: 'image',
        background_url: data.url
      }));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      // 1. Update project details
      await api.patch(`/projects/${project.id}`, {
        name: form.name.trim(),
        description: form.description,
        color: form.color,
        background_type: form.background_type,
        background_url: form.background_url,
        delegator_id: form.delegator_id || null,
      });

      // 2. Diff and update project members
      const currentMemberIds = members.map(m => m.id);
      const toAdd = form.member_ids.filter(id => !currentMemberIds.includes(id));
      // Leaders cannot be removed from project membership
      const toRemove = currentMemberIds.filter(id => !form.member_ids.includes(id) && members.find(m => m.id === id)?.role !== 'leader');

      await Promise.all([
        ...toAdd.map(uId => api.post(`/projects/${project.id}/members`, { user_id: uId })),
        ...toRemove.map(uId => api.delete(`/projects/${project.id}/members/${uId}`)),
      ]);

      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setBusy(false);
    }
  };

  const COLORS = ['#0079BF', '#D29034', '#519839', '#B04632', '#89609E', '#CD5A91', '#4BBF6B', '#00AECC'];
  const teamCandidates = allUsers.filter((m) => m.role !== 'leader' && m.is_active && m.status !== 'deleted' && m.status !== 'pending');
  const displayedCandidates = showOnlySelectedMembers
    ? teamCandidates.filter(m => form.member_ids.includes(m.id))
    : teamCandidates;
  const managerCandidates = allUsers.filter((m) => (m.role === 'manager' || m.role === 'leader') && m.is_active && m.status !== 'deleted' && m.status !== 'pending');
  const selectedCount = form.member_ids.filter(id => teamCandidates.some(tc => tc.id === id)).length;

  return (
    <Portal>
      <div className="tm-modal-overlay" onClick={onClose}>
        <div className="tm-modal" onClick={(e) => e.stopPropagation()} data-testid="edit-project-modal"
          style={{ maxWidth: 520, padding: 0, overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

          {/* Colored/Image banner header */}
          <div style={{
            padding: '20px 24px 24px',
            background: form.background_type === 'image' && form.background_url
              ? `url(${form.background_url}) center/cover`
              : `linear-gradient(135deg, ${form.color || '#0079BF'} 0%, ${(form.color || '#0079BF')}CC 100%)`,
            color: '#fff', position: 'relative',
          }}>
            <button onClick={onClose} data-testid="close-edit-project"
              style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: 999, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={16} />
            </button>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, opacity: 0.8, fontWeight: 700 }}>Edit board</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: '4px 0 0', letterSpacing: -0.3 }}>
              {form.name || 'Edit project'}
            </h2>
            {form.description && (
              <div style={{ fontSize: 13, marginTop: 4, opacity: 0.9 }}>{form.description}</div>
            )}
          </div>

          <form onSubmit={submit} style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
            {/* Name */}
            <div style={{ marginBottom: 16 }}>
              <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)' }}>PROJECT NAME</label>
              <input className="tm-input"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Marketing website 2026" required autoFocus
                style={{ fontSize: 15, padding: '10px 12px' }} />
            </div>

            {/* Description */}
            <div style={{ marginBottom: 18 }}>
              <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)' }}>DESCRIPTION</label>
              <textarea className="tm-input" rows={2}
                value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What is this project about? (optional)"
                style={{ fontSize: 14, padding: '10px 12px', resize: 'vertical' }} />
            </div>

            {/* Team members */}
            <div style={{ marginBottom: 16 }}>
              <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>TEAM MEMBERS</span>
                {selectedCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowOnlySelectedMembers(prev => !prev)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: form.color || '#0079BF',
                      fontWeight: 700,
                      cursor: 'pointer',
                      padding: 0,
                      textTransform: 'uppercase',
                      fontSize: 11
                    }}
                    data-testid="toggle-selected-members-filter"
                  >
                    {showOnlySelectedMembers ? 'Show all' : `${selectedCount} selected`}
                  </button>
                )}
              </label>
              {displayedCandidates.length === 0 ? (
                <div style={{ padding: 12, background: 'var(--trello-list-bg)', borderRadius: 6, fontSize: 13, color: 'var(--trello-text-muted)', textAlign: 'center' }}>
                  {showOnlySelectedMembers ? 'No selected members.' : 'No teammates yet.'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} data-testid="member-chips">
                  {displayedCandidates.map((m) => {
                    const on = form.member_ids.includes(m.id);
                    const initialsStr = (m.full_name || m.email).split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();
                    return (
                      <button key={m.id} type="button" onClick={() => toggleMember(m.id)} data-testid={`member-${m.id}`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 10px 6px 6px',
                          borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 500,
                          background: on ? (form.color || '#0079BF') : '#fff',
                          color: on ? '#fff' : '#000000',
                          border: on ? `2px solid ${form.color || '#0079BF'}` : '2px solid #DFE1E6',
                          transition: 'all 150ms',
                        }}>
                        <span style={{
                          width: 22, height: 22, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          background: m.avatar_url ? `url(${getAvatarUrl(m.avatar_url)}) center/cover` : (m.avatar_color || 'var(--trello-text-muted)'),
                          color: '#fff', fontSize: 10, fontWeight: 700,
                        }}>{!m.avatar_url && initialsStr}</span>
                        {(m.full_name || m.email.split('@')[0]).split(' ')[0]}
                        {on && <Check size={12} strokeWidth={3} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Advanced options */}
            <button type="button" onClick={() => setShowAdvanced((v) => !v)} data-testid="toggle-advanced"
              style={{ background: 'none', border: 'none', color: 'var(--trello-text-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <ChevronDown size={12} style={{ transform: showAdvanced ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
              {showAdvanced ? 'Hide' : 'Show'} advanced options
            </button>
            {showAdvanced && (
              <div style={{ marginTop: 12, padding: 12, background: 'var(--trello-list-bg)', borderRadius: 6 }}>
                <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)' }}>BOARD COLOR</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {COLORS.map((c) => (
                    <button type="button" key={c} onClick={() => setForm({ ...form, color: c, background_type: 'color', background_url: '' })} data-testid={`color-${c}`}
                      style={{
                        width: 34, height: 34, borderRadius: 8, background: c, cursor: 'pointer',
                        border: form.color === c ? '3px solid var(--trello-text)' : '2px solid #fff',
                        boxShadow: form.color === c ? `0 0 0 2px ${c}66` : 'none',
                        transition: 'all 120ms',
                      }} />
                  ))}
                </div>

                {/* Upload Banner */}
                <div style={{ marginTop: 14 }}>
                  <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)' }}>UPLOAD BANNER IMAGE</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                    <input
                      type="file"
                      accept="image/*"
                      id="edit-banner-upload"
                      onChange={handleBannerUpload}
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor="edit-banner-upload"
                      className="tm-btn"
                      style={{
                        background: form.color || '#0079BF',
                        color: '#fff',
                        cursor: 'pointer',
                        padding: '6px 12px',
                        fontSize: 13,
                        fontWeight: 600,
                        borderRadius: 4,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      {uploadingBanner ? 'Uploading...' : 'Choose image'}
                    </label>
                    {form.background_type === 'image' && form.background_url && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <img
                          src={form.background_url}
                          alt="Banner preview"
                          style={{ width: 48, height: 32, objectFit: 'cover', borderRadius: 4, border: '1px solid #DFE1E6' }}
                        />
                        <button
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, background_type: 'color', background_url: '', color: COLORS[0] }))}
                          style={{ background: 'none', border: 'none', color: '#DE350B', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)', marginTop: 12 }}>MANAGER (OPTIONAL)</label>
                <select className="tm-input" value={form.delegator_id} onChange={(e) => {
                  const managerId = e.target.value;
                  const newMemberIds = [...form.member_ids];
                  if (managerId && !newMemberIds.includes(managerId)) {
                    newMemberIds.push(managerId);
                  }
                  setForm({ ...form, delegator_id: managerId, member_ids: newMemberIds });
                }} data-testid="project-delegator">
                  <option value="">— No manager assigned —</option>
                  {managerCandidates.map((m) => (
                    <option key={m.id} value={m.id}>{m.full_name} · {m.role}</option>
                  ))}
                </select>
              </div>
            )}
          </form>

          {/* Action bar */}
          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--trello-border)', background: 'var(--trello-card-bg)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="tm-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" onClick={submit} className="tm-btn" disabled={busy || !form.name.trim()} data-testid="save-project-submit"
              style={{ background: form.color || '#0079BF', padding: '10px 20px', fontWeight: 700, letterSpacing: 0.2 }}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
